import React, { useState, useMemo } from 'react';
import { Button } from '../../components/ui/Button';
import { TelstraSeedSku, TelstraDistributorProduct, TelstraImportOptions } from '../../services/telstraDistributorService';

type SortField = 'sku' | 'name' | 'price' | 'soh' | 'status';
type SortDirection = 'asc' | 'desc';
type DeviceEligibility = 'outright_only' | 'mobile_voice' | 'mobile_broadband';

interface TelstraProductsTableProps {
  seedSkus: TelstraSeedSku[];
  products: TelstraDistributorProduct[];
  onSaveSkus: (skus: TelstraSeedSku[]) => Promise<boolean>;
  onDeleteSkusAndProducts: (skus: string[]) => Promise<boolean>;
  onImportProducts: (products: TelstraDistributorProduct[], options: TelstraImportOptions) => Promise<boolean>;
  onError: (error: string) => void;
  onSuccess: (message: string) => void;
}

interface CombinedProduct {
  sku: string;
  enabled: boolean;
  product: TelstraDistributorProduct | null;
  status: 'not_synced' | 'synced' | 'disabled';
}

export function TelstraProductsTable({
  seedSkus,
  products,
  onSaveSkus,
  onDeleteSkusAndProducts,
  onImportProducts,
  onError,
  onSuccess
}: TelstraProductsTableProps) {
  const [selectedSkus, setSelectedSkus] = useState<Set<string>>(new Set());
  const [searchFilter, setSearchFilter] = useState('');
  const [sortField, setSortField] = useState<SortField>('sku');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  
  const [showAddModal, setShowAddModal] = useState(false);
  const [bulkAddText, setBulkAddText] = useState('');
  const [newSku, setNewSku] = useState('');
  
  const [showImportModal, setShowImportModal] = useState(false);
  const [deviceEligibility, setDeviceEligibility] = useState<DeviceEligibility>('outright_only');
  const [importing, setImporting] = useState(false);
  
  const [loading, setLoading] = useState(false);

  // Create product lookup by SKU
  const productMap = useMemo(() => {
    const map = new Map<string, TelstraDistributorProduct>();
    products.forEach(p => map.set(p.sku, p));
    return map;
  }, [products]);

  // Combine seed SKUs with product data
  const combinedProducts = useMemo((): CombinedProduct[] => {
    return seedSkus.map(s => ({
      sku: s.sku,
      enabled: s.enabled,
      product: productMap.get(s.sku) || null,
      status: !s.enabled ? 'disabled' as const : productMap.has(s.sku) ? 'synced' as const : 'not_synced' as const
    }));
  }, [seedSkus, productMap]);

  // Filter and sort
  const filteredProducts = useMemo(() => {
    let result = [...combinedProducts];

    if (searchFilter.trim()) {
      const query = searchFilter.toLowerCase();
      result = result.filter(item =>
        item.sku.toLowerCase().includes(query) ||
        (item.product?.name || '').toLowerCase().includes(query)
      );
    }

    result.sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'sku':
          comparison = a.sku.localeCompare(b.sku);
          break;
        case 'name':
          comparison = (a.product?.name || '').localeCompare(b.product?.name || '');
          break;
        case 'price':
          comparison = (a.product?.rrpIncGst || 0) - (b.product?.rrpIncGst || 0);
          break;
        case 'soh':
          comparison = (a.product?.soh || 0) - (b.product?.soh || 0);
          break;
        case 'status':
          comparison = a.status.localeCompare(b.status);
          break;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [combinedProducts, searchFilter, sortField, sortDirection]);

  const syncedProducts = combinedProducts.filter(p => p.status === 'synced');
  const enabledCount = seedSkus.filter(s => s.enabled).length;
  const allSelected = filteredProducts.length > 0 && filteredProducts.every(p => selectedSkus.has(p.sku));
  const someSelected = selectedSkus.size > 0;
  const selectedSynced = Array.from(selectedSkus).filter(sku => productMap.has(sku));

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedSkus(new Set());
    } else {
      setSelectedSkus(new Set(filteredProducts.map(p => p.sku)));
    }
  };

  const toggleSelect = (sku: string) => {
    setSelectedSkus(prev => {
      const next = new Set(prev);
      if (next.has(sku)) {
        next.delete(sku);
      } else {
        next.add(sku);
      }
      return next;
    });
  };

  const handleAddSku = async () => {
    const sku = newSku.trim().toUpperCase();
    if (!sku) return;

    if (seedSkus.some(s => s.sku === sku)) {
      onError(`SKU ${sku} is already tracked`);
      return;
    }

    setLoading(true);
    const updatedSkus: TelstraSeedSku[] = [...seedSkus, { sku, enabled: true, name: null }];
    const success = await onSaveSkus(updatedSkus);
    setLoading(false);

    if (success) {
      setNewSku('');
      onSuccess(`Added SKU ${sku}`);
    }
  };

  const handleBulkAdd = async () => {
    const lines = bulkAddText.split(/[\n,;]+/).map(s => s.trim().toUpperCase()).filter(Boolean);
    const existingSkus = new Set(seedSkus.map(s => s.sku));
    
    // Dedupe the input lines AND filter out existing SKUs
    const uniqueNewSkus: string[] = [];
    const seen = new Set<string>();
    for (const sku of lines) {
      if (!seen.has(sku) && !existingSkus.has(sku)) {
        seen.add(sku);
        uniqueNewSkus.push(sku);
      }
    }

    if (uniqueNewSkus.length === 0) {
      onError('All SKUs are already tracked or duplicates');
      return;
    }

    setLoading(true);
    const updatedSkus: TelstraSeedSku[] = [
      ...seedSkus,
      ...uniqueNewSkus.map(sku => ({ sku, enabled: true, name: null }))
    ];
    const success = await onSaveSkus(updatedSkus);
    setLoading(false);

    if (success) {
      setBulkAddText('');
      setShowAddModal(false);
      onSuccess(`Added ${uniqueNewSkus.length} SKUs`);
    }
  };

  const handleAddAllFromUpload = async () => {
    const existingSkus = new Set(seedSkus.map(s => s.sku));
    const newSkus = products
      .filter(p => !existingSkus.has(p.sku))
      .map(p => ({ sku: p.sku, enabled: true, name: p.name }));

    // Dedupe
    const uniqueNewSkus: TelstraSeedSku[] = [];
    const seen = new Set<string>();
    for (const s of newSkus) {
      if (!seen.has(s.sku)) {
        seen.add(s.sku);
        uniqueNewSkus.push(s);
      }
    }

    if (uniqueNewSkus.length === 0) {
      onError('All products from upload are already tracked');
      return;
    }

    setLoading(true);
    const updatedSkus: TelstraSeedSku[] = [...seedSkus, ...uniqueNewSkus];
    const success = await onSaveSkus(updatedSkus);
    setLoading(false);

    if (success) {
      onSuccess(`Added ${uniqueNewSkus.length} SKUs from upload`);
    }
  };

  const handleToggle = async (sku: string) => {
    setLoading(true);
    const updatedSkus = seedSkus.map(s => 
      s.sku === sku ? { ...s, enabled: !s.enabled } : s
    );
    await onSaveSkus(updatedSkus);
    setLoading(false);
  };

  const handleEnableSelected = async () => {
    setLoading(true);
    const updatedSkus = seedSkus.map(s => 
      selectedSkus.has(s.sku) ? { ...s, enabled: true } : s
    );
    const success = await onSaveSkus(updatedSkus);
    setLoading(false);
    if (success) onSuccess(`Enabled ${selectedSkus.size} SKUs`);
  };

  const handleDisableSelected = async () => {
    setLoading(true);
    const updatedSkus = seedSkus.map(s => 
      selectedSkus.has(s.sku) ? { ...s, enabled: false } : s
    );
    const success = await onSaveSkus(updatedSkus);
    setLoading(false);
    if (success) onSuccess(`Disabled ${selectedSkus.size} SKUs`);
  };

  const handleRemoveSelected = async () => {
    if (!window.confirm(`Remove ${selectedSkus.size} SKU${selectedSkus.size > 1 ? 's' : ''}?`)) return;

    setLoading(true);
    const success = await onDeleteSkusAndProducts(Array.from(selectedSkus));
    setLoading(false);

    if (success) {
      setSelectedSkus(new Set());
      onSuccess(`Removed ${selectedSkus.size} SKUs`);
    }
  };

  const handleOpenImportModal = () => {
    if (selectedSynced.length === 0) {
      onError('No synced products selected');
      return;
    }
    setShowImportModal(true);
  };

  const handleImport = async () => {
    setImporting(true);

    const productsToImport = selectedSynced
      .map(sku => productMap.get(sku))
      .filter((p): p is TelstraDistributorProduct => p !== undefined);

    const success = await onImportProducts(productsToImport, { deviceEligibility });

    setImporting(false);
    if (success) {
      setSelectedSkus(new Set());
      setShowImportModal(false);
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <span className="text-neutral-300">↕</span>;
    return <span className="text-primary-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>;
  };

  const getStatusBadge = (status: CombinedProduct['status']) => {
    switch (status) {
      case 'synced':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700">Has Data</span>;
      case 'not_synced':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">No Data</span>;
      case 'disabled':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-neutral-100 text-neutral-500">Disabled</span>;
    }
  };

  const formatPrice = (price: number | undefined) => {
    if (price === undefined || price === null) return '-';
    return `$${price.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <div className="bg-white border border-neutral-200 rounded-xl shadow-sm overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-neutral-200">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-neutral-900">Tracked Products ({seedSkus.length})</h2>
            <p className="text-sm text-neutral-500 mt-1">{enabledCount} enabled • {syncedProducts.length} with data</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setShowAddModal(true)}>
              <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add SKUs
            </Button>
            {products.length > 0 && (
              <Button variant="outline" onClick={handleAddAllFromUpload} disabled={loading}>
                Add All from Upload
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Quick Add */}
      <div className="px-6 py-4 bg-neutral-50 border-b border-neutral-200">
        <div className="flex items-center gap-3">
          <input
            type="text"
            value={newSku}
            onChange={(e) => setNewSku(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === 'Enter' && handleAddSku()}
            placeholder="Enter SKU (e.g. APP17256)"
            className="flex-1 px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm font-mono"
          />
          <Button onClick={handleAddSku} disabled={!newSku.trim() || loading}>Add</Button>
        </div>
      </div>

      {/* Search and Actions */}
      <div className="px-6 py-4 border-b border-neutral-200">
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-sm">
            <input
              type="text"
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              placeholder="Search SKUs, names..."
              className="w-full pl-9 pr-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
            />
            <svg className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>

          {someSelected && (
            <div className="flex items-center gap-2 border-l border-neutral-200 pl-4">
              <span className="text-sm text-neutral-600 font-medium">{selectedSkus.size} selected</span>
              <Button size="sm" variant="outline" onClick={handleEnableSelected} disabled={loading}>Enable</Button>
              <Button size="sm" variant="outline" onClick={handleDisableSelected} disabled={loading}>Disable</Button>
              {selectedSynced.length > 0 && (
                <Button size="sm" onClick={handleOpenImportModal}>Import ({selectedSynced.length})</Button>
              )}
              <Button size="sm" variant="outline" onClick={handleRemoveSelected} disabled={loading} className="text-rose-600 hover:text-rose-700 border-rose-200">Remove</Button>
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      {filteredProducts.length > 0 ? (
        <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
          <table className="w-full">
            <thead className="bg-neutral-50 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-left w-12">
                  <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} className="h-4 w-4 rounded border-neutral-300 text-primary-600" aria-label="Select all" />
                </th>
                <th className="px-4 py-3 text-left w-12">
                  <span className="text-xs font-medium text-neutral-500 uppercase">On</span>
                </th>
                <th className="px-4 py-3 text-left">
                  <button onClick={() => handleSort('sku')} className="flex items-center gap-1 text-xs font-medium text-neutral-500 uppercase hover:text-neutral-700">
                    SKU <SortIcon field="sku" />
                  </button>
                </th>
                <th className="px-4 py-3 text-left max-w-xs">
                  <button onClick={() => handleSort('name')} className="flex items-center gap-1 text-xs font-medium text-neutral-500 uppercase hover:text-neutral-700">
                    Name <SortIcon field="name" />
                  </button>
                </th>
                <th className="px-4 py-3 text-right">
                  <button onClick={() => handleSort('price')} className="flex items-center gap-1 text-xs font-medium text-neutral-500 uppercase hover:text-neutral-700 ml-auto">
                    RRP (inc GST) <SortIcon field="price" />
                  </button>
                </th>
                <th className="px-4 py-3 text-right">
                  <button onClick={() => handleSort('soh')} className="flex items-center gap-1 text-xs font-medium text-neutral-500 uppercase hover:text-neutral-700 ml-auto">
                    SOH <SortIcon field="soh" />
                  </button>
                </th>
                <th className="px-4 py-3 text-left">
                  <button onClick={() => handleSort('status')} className="flex items-center gap-1 text-xs font-medium text-neutral-500 uppercase hover:text-neutral-700">
                    Status <SortIcon field="status" />
                  </button>
                </th>
                <th className="px-4 py-3 w-12"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200">
              {filteredProducts.map((item) => (
                <tr key={item.sku} className={`hover:bg-neutral-50 ${!item.enabled ? 'opacity-50' : ''}`}>
                  <td className="px-4 py-3">
                    <input type="checkbox" checked={selectedSkus.has(item.sku)} onChange={() => toggleSelect(item.sku)} className="h-4 w-4 rounded border-neutral-300 text-primary-600" aria-label={`Select ${item.sku}`} />
                  </td>
                  <td className="px-4 py-3">
                    <input type="checkbox" checked={item.enabled} onChange={() => handleToggle(item.sku)} className="h-4 w-4 rounded border-neutral-300 text-emerald-600" title="Enable/disable" />
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-mono text-sm font-medium text-neutral-900">{item.sku}</span>
                  </td>
                  <td className="px-4 py-3 max-w-xs">
                    {item.product?.name ? (
                      <span className="text-sm text-neutral-600 truncate block" title={item.product.name}>{item.product.name}</span>
                    ) : (
                      <span className="text-sm text-neutral-400 italic">Upload to fetch name</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-sm text-neutral-900">{formatPrice(item.product?.rrpIncGst)}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-sm text-neutral-900">{item.product?.soh ?? '-'}</span>
                  </td>
                  <td className="px-4 py-3">{getStatusBadge(item.status)}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={async () => {
                        if (window.confirm(`Remove SKU ${item.sku}?`)) {
                          await onDeleteSkusAndProducts([item.sku]);
                        }
                      }}
                      className="text-neutral-400 hover:text-rose-600"
                      title="Remove"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : seedSkus.length > 0 ? (
        <div className="text-center py-12 text-neutral-500">
          <p className="text-sm">No products match your search.</p>
        </div>
      ) : (
        <div className="text-center py-12 text-neutral-500">
          <svg className="w-12 h-12 mx-auto text-neutral-300 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
          <p className="text-sm">No SKUs tracked yet.</p>
          <p className="text-xs text-neutral-400 mt-1">Upload a Telstra spreadsheet or add SKUs manually.</p>
        </div>
      )}

      {/* Footer */}
      {seedSkus.length > 0 && (
        <div className="px-6 py-4 bg-neutral-50 border-t border-neutral-200 flex items-center justify-between text-sm text-neutral-500">
          <span>{filteredProducts.length} of {seedSkus.length} products{searchFilter && ` matching "${searchFilter}"`}</span>
          <span>{syncedProducts.length} with data</span>
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowAddModal(false)} />
            <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg">
              <div className="px-6 py-5">
                <h3 className="text-lg font-semibold">Bulk Add SKUs</h3>
                <div className="mt-4">
                  <textarea
                    value={bulkAddText}
                    onChange={(e) => setBulkAddText(e.target.value.toUpperCase())}
                    placeholder="APP17256&#10;APP17512&#10;APP17128"
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
                  />
                </div>
              </div>
              <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 rounded-b-xl">
                <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
                <Button onClick={handleBulkAdd} disabled={!bulkAddText.trim() || loading}>Add SKUs</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowImportModal(false)} />
            <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg">
              <div className="px-6 py-5">
                <h3 className="text-lg font-semibold">Import {selectedSynced.length} Products</h3>
                <div className="mt-6 space-y-4">
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-800">
                      <strong>Pricing:</strong> Products are sold at the RRP from the spreadsheet (inc GST). This price is fixed and cannot be changed.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Device Eligibility</label>
                    <div className="mt-3 space-y-2">
                      {[
                        { value: 'outright_only', label: 'Outright Only', desc: 'Standalone purchase only' },
                        { value: 'mobile_voice', label: 'Mobile Voice', desc: 'Can attach to voice plans' },
                        { value: 'mobile_broadband', label: 'Mobile Broadband', desc: 'Can attach to data plans' }
                      ].map(opt => (
                        <label key={opt.value} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border cursor-pointer hover:bg-gray-100">
                          <input type="radio" name="eligibility" value={opt.value} checked={deviceEligibility === opt.value} onChange={(e) => setDeviceEligibility(e.target.value as DeviceEligibility)} className="h-4 w-4 text-primary-600" />
                          <div>
                            <span className="text-sm font-medium">{opt.label}</span>
                            <p className="text-xs text-gray-500">{opt.desc}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">Products will be imported as <strong>Telstra handsets</strong>.</p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 rounded-b-xl">
                <Button variant="outline" onClick={() => setShowImportModal(false)}>Cancel</Button>
                <Button onClick={handleImport} disabled={importing}>{importing ? 'Importing...' : 'Import'}</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TelstraProductsTable;
